import { useEffect, useState, useRef } from "react";
import { ArrowUp, ArrowDown, Zap } from "lucide-react";

// MarketFeed — a small "notable events" ticker separate from the raw trade
// tape. Shows the last 5 aggressive (top-10%) trades formatted as callouts.
//
// Pulls from tradesRef (already flagged with `aggressive: true`) every ~500ms.
// Older entries fade to lower opacity.
const MAX_ENTRIES = 5;

const fmtCallout = (usd) => {
  const abs = Math.abs(usd);
  if (abs >= 1e6) return "$" + (abs / 1e6).toFixed(2) + "M";
  if (abs >= 1e3) return "$" + (abs / 1e3).toFixed(1) + "K";
  return "$" + abs.toFixed(0);
};
const fmtPrice = (p) => {
  const abs = Math.abs(p);
  const d = abs >= 100 ? 2 : abs >= 1 ? 4 : abs >= 0.01 ? 6 : 8;
  return p.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
};

export default function MarketFeed({ tradesRef, pair, isMobile, resetKey }) {
  const [entries, setEntries] = useState([]);
  const seenIdsRef = useRef(new Set());
  const resetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (resetKeyRef.current !== resetKey) {
      resetKeyRef.current = resetKey;
      seenIdsRef.current = new Set();
      setEntries([]);
    }
  }, [resetKey]);

  useEffect(() => {
    const id = setInterval(() => {
      const trades = tradesRef?.current;
      if (!trades || trades.length === 0) return;
      const seen = seenIdsRef.current;
      const fresh = [];
      for (const t of trades) {
        if (!t.aggressive) continue;
        if (seen.has(t.id)) break; // rest are older
        seen.add(t.id);
        fresh.push({
          id: t.id,
          isBuy: !t.isBuyerMaker,
          usd: t.price * t.qty,
          price: t.price,
          ts: t.timestamp,
        });
        if (fresh.length + entries.length > MAX_ENTRIES * 3) break;
      }
      if (fresh.length === 0) return;
      // Cap seen set to avoid unbounded growth
      if (seen.size > 500) {
        seenIdsRef.current = new Set(Array.from(seen).slice(-250));
      }
      setEntries((prev) => [...fresh, ...prev].slice(0, MAX_ENTRIES));
    }, 400);
    return () => clearInterval(id);
  }, [tradesRef, entries.length]);

  const position = isMobile
    ? "left-3 right-3 bottom-[calc(140px+env(safe-area-inset-bottom))]"
    : "right-[336px] lg:right-[352px] bottom-16";

  return (
    <div
      className={`absolute z-30 ${position} pointer-events-none flex flex-col gap-1 max-w-[280px] ml-auto`}
      data-testid="market-feed"
    >
      <div className="flex items-center gap-2 mb-1 justify-end">
        <Zap className="w-3 h-3 text-[#FFE480]" />
        <span
          className="font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-400"
          data-testid="market-feed-label"
        >
          Market Feed
        </span>
      </div>
      {entries.length === 0 && (
        <div
          className="cb-hud-chip !bg-black/50 backdrop-blur-md px-3 py-2 text-right"
          data-testid="market-feed-empty"
        >
          <span className="font-mono-tac text-[10px] text-slate-500 uppercase tracking-[0.24em]">
            Awaiting significant trades…
          </span>
        </div>
      )}
      {entries.map((e, i) => {
        const opacity = 1 - i * 0.15;
        const color = e.isBuy ? "#00FFA3" : "#FF2A2A";
        const label = e.isBuy ? "Large buy" : "Large sell";
        const Icon = e.isBuy ? ArrowUp : ArrowDown;
        return (
          <div
            key={e.id}
            className="cb-hud-chip !bg-black/50 backdrop-blur-md px-3 py-1.5 flex items-center gap-2 justify-end"
            style={{ opacity }}
            data-testid={`market-feed-row-${i}`}
          >
            <Icon className="w-3 h-3 flex-shrink-0" style={{ color }} />
            <span
              className="font-mono-tac text-[10px] uppercase tracking-[0.24em]"
              style={{ color }}
              data-testid={`market-feed-side-${i}`}
            >
              {label}
            </span>
            <span
              className="font-mono-tac text-[11px] tabular-nums text-white"
              data-testid={`market-feed-notional-${i}`}
            >
              {fmtCallout(e.usd)}
            </span>
            <span className="font-mono-tac text-[9px] text-slate-500">@</span>
            <span
              className="font-mono-tac text-[10px] tabular-nums text-slate-300"
              data-testid={`market-feed-price-${i}`}
            >
              {fmtPrice(e.price)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
